import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { Alert, Badge, Button, Card, Dropdown, Label, Tabs } from 'flowbite-react';
import { Suspense, lazy, useRef, useState } from 'react';
import { HiInformationCircle } from 'react-icons/hi';
import { Link, useLoaderData, useOutletContext } from 'react-router-dom';

import type { LoaderData, loaders } from 'src/api/loaders';
import type { Store, UseCase } from 'src/api/models';
import { mutations } from 'src/api/mutations';
import { queries } from 'src/api/queries';
import { FeedbackTable } from 'src/components/feedback-table';
import { LoadingSpinner } from 'src/components/loading-spinner';
import { MetricsHeaderChart } from 'src/components/metrics-header-chart';
import { fileExtension } from 'src/utils';

import { DashboardContext } from './container';

const FileUploadDropzone = lazy(() =>
  import('src/components/file-upload-dropzone').then((module) => ({
    default: module.FileUploadDropzone,
  })),
);

export function UseCaseHeader({
  usecase,
  badgeText,
  badgeColor,
}: {
  usecase: UseCase;
  badgeText: string;
  badgeColor: string;
}) {
  return (
    <>
      <div className='w-full text-center align-middle'>
        <div className='relative h-6'>
          <div className='absolute'>
            <nav className='flex bg-gray-50 py-5 text-gray-700' aria-label='Breadcrumb'>
              <ol className='inline-flex items-center space-x-1 md:space-x-3'>
                <li className='inline-flex items-center'>
                  <Link
                    to={`../video-flows`}
                    className='inline-flex items-center text-sm font-medium text-gray-700 hover:text-blue-600'
                  >
                    <FontAwesomeIcon icon={solid('film')} className='mr-2.5 h-3 w-3' />
                    Video Flows
                  </Link>
                </li>
                <li aria-current='page'>
                  <div className='flex items-center'>
                    <svg
                      className='mx-1 h-3 w-3 text-gray-400'
                      aria-hidden='true'
                      xmlns='http://www.w3.org/2000/svg'
                      fill='none'
                      viewBox='0 0 6 10'
                    >
                      <path
                        stroke='currentColor'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth='2'
                        d='m1 9 4-4-4-4'
                      />
                    </svg>
                    <span className='ml-1 text-sm font-medium text-gray-500 md:ml-2'>
                      {usecase.pretty_name}
                    </span>
                  </div>
                </li>
              </ol>
            </nav>
          </div>
        </div>
      </div>
      <div className='relative h-6 py-2'>
        <div className='absolute w-full'>
          <span className='text-2xl font-medium'>{usecase.pretty_name}</span>
          <Badge color={badgeColor} size='2xl' className='float-right inline-block'>
            {badgeText}
          </Badge>
        </div>
      </div>
    </>
  );
}

const mediaFileNameFromInput = (template: string, inputFilename: string): string => {
  return `${template}.${fileExtension(inputFilename)}`;
};

function UploadVideo({ usecase }: { usecase: UseCase }) {
  const uploadUrlMutation = useMutation(mutations.signedMediaUploadUrlForUseCase());
  const didUploadMediaMutation = useMutation(mutations.didUploadUseCaseMedia());
  const useCaseOnboardingStateMutation = useMutation(mutations.useCaseOnboardingState());
  const uploadFilenames = useRef({ baseVideoUploadFilename: '', scriptVideoUploadFilename: '' });
  const [baseVideo, setBaseVideo] = useState(usecase.template_video_signed_url);
  const [scriptVideo, setScriptVideo] = useState(usecase.script_video_signed_url);
  const [baseVideoUploadInProgress, setBaseVideoUploadInProgress] = useState(false);
  const [scriptVideoUploadInProgress, setScriptVideoUploadInProgress] = useState(false);
  const [hasBaseVideoUploadError, setHasBaseVideoUploadError] = useState(false);
  const [hasScriptVideoUploadError, setHasScriptVideoUploadError] = useState(false);

  return (
    <>
      {usecase.onboarding_state == 'VIDEOS_RECEIVED' && <UseCaseProcessing usecase={usecase} />}
      {usecase.onboarding_state != 'VIDEOS_RECEIVED' && (
        <div className='flex w-full flex-col gap-6'>
          {!baseVideoUploadInProgress &&
            !scriptVideoUploadInProgress &&
            baseVideo &&
            scriptVideo && (
              <UseCaseHeader
                usecase={usecase}
                badgeText='Click Start Processing My Videos Below!'
                badgeColor='yellow'
              />
            )}
          {(baseVideoUploadInProgress ||
            scriptVideoUploadInProgress ||
            !baseVideo ||
            !scriptVideo) && (
            <UseCaseHeader
              usecase={usecase}
              badgeText='Waiting for video upload'
              badgeColor='yellow'
            />
          )}
          <div className='flex w-full gap-6'>
            <Card className='h-auto w-1/2 grow gap-6'>
              <div className='text-md mb-4 font-medium text-gray-900'>
                <span>Recording Instructions</span>
              </div>
              <div className='w-full'>
                <video
                  className='h-auto w-full max-w-full rounded-lg border border-gray-200'
                  controls
                >
                  <source
                    src='https://storage.googleapis.com/mavid-311321.appspot.com/Maverick%20-%20Onboarding%20Video.mp4'
                    type='video/mp4'
                  />
                </video>
              </div>
              <div className='text-md font-medium text-gray-900'>
                This is the part where you record your awesome videos!
              </div>
              <div>
                View the instructions for how to record your videos below. Check out the video above
                for a quick explainer.
              </div>
              <div>
                <p>
                  If you&apos;d like to see some best practices and sample videos for different use
                  cases, click on the Get Inspiration button below!
                </p>
              </div>
              <div>
                <Link to={`generate-script`} className='mr-4 inline-flex'>
                  <Button className='w-52' gradientDuoTone='redToYellow'>
                    Generate Script with AI
                  </Button>
                </Link>
                <a
                  href='https://trymaverick.notion.site/Onboarding-Instructions-3f91edea8e74409dbc855af9039a9153'
                  target='_blank'
                  className='group relative mr-2 inline-flex items-center justify-center overflow-hidden rounded-lg bg-gradient-to-br from-purple-600 to-blue-500 p-0.5 text-sm font-medium text-gray-900 hover:text-white focus:outline-none focus:ring-4 focus:ring-blue-300 group-hover:from-purple-600 group-hover:to-blue-500'
                  rel='noreferrer'
                >
                  <span className='relative rounded-md bg-white px-5 py-2.5 transition-all duration-75 ease-in group-hover:bg-opacity-0 '>
                    View Instructions
                  </span>
                </a>
                <a
                  href='https://trymaverick.notion.site/Video-Inspiration-75ea59fff7674bab8c75aa71fbd57ce4'
                  target='_blank'
                  className='group relative mr-2 inline-flex items-center justify-center overflow-hidden rounded-lg bg-gradient-to-br from-purple-600 to-blue-500 p-0.5 text-sm font-medium text-gray-900 hover:text-white focus:outline-none focus:ring-4 focus:ring-blue-300 group-hover:from-purple-600 group-hover:to-blue-500'
                  rel='noreferrer'
                >
                  <span className='relative rounded-md bg-white px-5 py-2.5 transition-all duration-75 ease-in group-hover:bg-opacity-0 '>
                    Get Inspiration
                  </span>
                </a>
              </div>
            </Card>
            <Card className='min-h-full w-1/2'>
              <div className='flex grow flex-col justify-between gap-6'>
                <div className='text-md mb-4 font-medium text-gray-900'>
                  <span>Upload Videos</span>
                </div>
                {hasBaseVideoUploadError && (
                  <Alert color='failure' icon={HiInformationCircle}>
                    <span>
                      <p>
                        <span className='font-medium'>
                          There was an issue with the base video upload
                        </span>
                      </p>
                    </span>
                  </Alert>
                )}
                {hasScriptVideoUploadError && (
                  <Alert color='failure' icon={HiInformationCircle}>
                    <span>
                      <p>
                        <span className='font-medium'>
                          There was an issue with the script video upload
                        </span>
                      </p>
                    </span>
                  </Alert>
                )}
                <div>
                  <Label value={'Upload Base Video file'} />
                  <Suspense>
                    <FileUploadDropzone
                      subtitle='Upload a MOV, MP4 file'
                      allowedExtensions={['mp4', 'mov']}
                      allowReplacingExistingFile={true}
                      accept='video/quicktime,video/mp4'
                      method='PUT'
                      existingFile={baseVideo}
                      hasExistingFile={!!baseVideo}
                      uploadUrl={async (inputName) => {
                        setBaseVideoUploadInProgress(true);
                        setHasBaseVideoUploadError(false);
                        const { signed_url, upload_filename } = await uploadUrlMutation.mutateAsync(
                          [usecase.id, mediaFileNameFromInput('baseVideo', inputName)],
                        );
                        uploadFilenames.current.baseVideoUploadFilename = upload_filename;
                        return signed_url;
                      }}
                      onStart={async () => {
                        setBaseVideo('');
                        return true;
                      }}
                      onComplete={async (inputName) => {
                        try {
                          const uploadMediaResults = await didUploadMediaMutation.mutateAsync([
                            usecase.id,
                            mediaFileNameFromInput('baseVideo', inputName),
                            uploadFilenames.current.baseVideoUploadFilename,
                          ]);
                          setBaseVideo(uploadMediaResults.template_video_signed_url);
                        } catch (error) {
                          console.error(error);
                          setHasBaseVideoUploadError(true);
                        } finally {
                          setBaseVideoUploadInProgress(false);
                        }
                        return true;
                      }}
                    />
                  </Suspense>
                </div>
                <div>
                  <Label value={'Upload Script Video file'} />
                  <Suspense>
                    <FileUploadDropzone
                      subtitle='Upload a MOV, MP4, or WAV file'
                      allowedExtensions={['wav', 'mp4', 'mov']}
                      allowReplacingExistingFile={true}
                      accept='video/quicktime,video/mp4,audio/wav'
                      method='PUT'
                      existingFile={scriptVideo}
                      hasExistingFile={!!scriptVideo}
                      uploadUrl={async (inputName) => {
                        setScriptVideoUploadInProgress(true);
                        setHasScriptVideoUploadError(false);
                        const { signed_url, upload_filename } = await uploadUrlMutation.mutateAsync(
                          [usecase.id, mediaFileNameFromInput('recordedNames', inputName)],
                        );
                        uploadFilenames.current.scriptVideoUploadFilename = upload_filename;
                        return signed_url;
                      }}
                      onStart={async () => {
                        setScriptVideo('');
                        return true;
                      }}
                      onComplete={async (inputName) => {
                        try {
                          const uploadMediaResults = await didUploadMediaMutation.mutateAsync([
                            usecase.id,
                            mediaFileNameFromInput('recordedNames', inputName),
                            uploadFilenames.current.scriptVideoUploadFilename,
                          ]);
                          setScriptVideo(uploadMediaResults.script_video_signed_url);
                        } catch (error) {
                          console.log(error);
                          setHasScriptVideoUploadError(true);
                        } finally {
                          setScriptVideoUploadInProgress(false);
                        }
                        return true;
                      }}
                    />
                  </Suspense>
                </div>
                <div>
                  <Button
                    disabled={
                      baseVideoUploadInProgress ||
                      scriptVideoUploadInProgress ||
                      !scriptVideo ||
                      !baseVideo
                    }
                    onClick={async () => {
                      useCaseOnboardingStateMutation
                        .mutateAsync([usecase.id, 'VIDEOS_RECEIVED'])
                        .then(() => {
                          usecase.onboarding_state = 'VIDEOS_RECEIVED';
                        })
                        .catch((res) => {
                          console.log(res);
                        });
                    }}
                  >
                    {(baseVideoUploadInProgress ||
                      scriptVideoUploadInProgress ||
                      didUploadMediaMutation.isPending) && (
                      <FontAwesomeIcon icon={solid('spinner')} spin className='mr-3' />
                    )}
                    {!baseVideoUploadInProgress &&
                      !scriptVideoUploadInProgress &&
                      scriptVideo &&
                      baseVideo && (
                        <FontAwesomeIcon icon={solid('circle-check')} className='mr-3' />
                      )}
                    Start Processing My Videos
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}
    </>
  );
}

function UseCaseProcessing({ usecase }: { usecase: UseCase }) {
  return (
    <div className='flex w-full flex-col gap-6'>
      <UseCaseHeader usecase={usecase} badgeText='Videos Received' badgeColor='purple' />
      <div className='flex h-96 flex-row gap-6'>
        <Card className='h-full w-1/3'>
          <div className='text-md font-medium text-gray-900'>
            <span>Base Video</span>
          </div>
          <video className='mx-auto inline-block h-72' controls>
            <source src={usecase.template_video_signed_url ?? ''} />
          </video>
        </Card>
        <Card className='w-2/3'>
          <div className='text-md flex w-full justify-between font-medium text-gray-900'>
            <span>Videos Received</span>
          </div>
          <div className='flex grow flex-col justify-center align-middle'>
            We&rsquo;ve received your videos and are currently setting them up! You&rsquo;ll get an
            email when your videos are ready to go live along with instructions on how to complete
            your Klaviyo/email template integration.
          </div>
        </Card>
      </div>
    </div>
  );
}

function UseCaseIntegration({ usecase }: { usecase: UseCase }) {
  return (
    <div className='flex w-full flex-col gap-6'>
      <UseCaseHeader usecase={usecase} badgeText='Waiting For Integration' badgeColor='yellow' />
      <div className='flex h-96 flex-row gap-6'>
        <Card className='h-full w-1/3'>
          <div className='text-md font-medium text-gray-900'>
            <span>Base Video</span>
          </div>
          <video className='mx-auto inline-block h-72' controls>
            <source src={usecase.template_video_signed_url ?? ''} />
          </video>
        </Card>
        <Card className='grow'>
          <span className='text-xl font-medium'>Klaviyo Setup Instructions</span>
          <Tabs style='fullWidth'>
            <Tabs.Item active title='Maverick Template'>
              <ol className='list-inside list-decimal space-y-6'>
                <li>
                  Within your {usecase.pretty_name} flow, add an email action for the Maverick
                  video.
                </li>
                <li>Add the subject line &ldquo;A video just for you from our founder!&rdquo;</li>
                <li>
                  Change the template for the template named &ldquo;Maverick - {usecase.pretty_name}
                  .&rdquo;
                </li>
                <li>
                  Set your email action live and watch your Maverick video delight your customers!
                </li>
              </ol>
            </Tabs.Item>
            <Tabs.Item title='Your Template'>
              <ol className='list-inside list-decimal space-y-6'>
                <li>
                  Within your {usecase.pretty_name} flow, select/add the email action you want to
                  add the Maverick video to.
                </li>
                <li>
                  Add{' '}
                  <a href={usecase.email_template_info_img_url} className='text-blue-600'>
                    this thumbnail
                  </a>{' '}
                  as an image to the email template.{' '}
                </li>
                <li>
                  Add{' '}
                  <a href={usecase.customer_video_lookup_url ?? ''} className='text-blue-600'>
                    this link
                  </a>{' '}
                  to the thumbnail.
                </li>
                <li>
                  Add a button that says &ldquo;WATCH VIDEO&rdquo; below the thumbnail and add that
                  link to it.
                </li>
                <li>
                  Set your email action live and watch your Maverick video delight your customers!
                </li>
              </ol>
            </Tabs.Item>
          </Tabs>
        </Card>
      </div>
    </div>
  );
}

function UseCaseLive({ usecase, store }: { usecase: UseCase; store: Store }) {
  const [metricDays, setMetricDays] = useState(30);
  const [page, setPage] = useState(1);
  const { isLoading: isLoadingFeedbacks, data: feedbacks } = useQuery({
    ...queries.customerFeedbacksForUsecase(usecase.id, page, 10),
    placeholderData: keepPreviousData,
  });
  const { isLoading: metricsAreLoading, data: aggregatedMetrics } = useQuery({
    ...queries.storeAggregatedMetrics(store.key_name, metricDays, usecase.id),
    placeholderData: keepPreviousData,
  });
  const { data: dailyMetrics } = useQuery({
    ...queries.storeDailyMetrics(store.key_name, metricDays, usecase.id),
    placeholderData: keepPreviousData,
  });

  return (
    <div className='flex w-full flex-col gap-6'>
      <UseCaseHeader usecase={usecase} badgeText='Live' badgeColor='green'></UseCaseHeader>
      <div className='flex w-full grow flex-col gap-6'>
        <div className='flex h-96 flex-row gap-6'>
          <Card className='h-full w-1/3'>
            <div className='text-md font-medium text-gray-900'>
              <span>Base Videos</span>
            </div>
            <video className='mx-auto inline-block h-72' controls>
              <source src={usecase.template_video_signed_url ?? ''} />
            </video>
          </Card>
          <Card className='w-2/3'>
            <div className='text-md flex w-full justify-between font-medium text-gray-900'>
              <span>Use Case Analytics</span>
              <div className='z-20 flex gap-5'>
                <Dropdown
                  label={`${metricDays} Days`}
                  renderTrigger={() => (
                    <div className='w-fit text-sm font-semibold text-gray-500 hover:text-gray-950'>
                      {`${metricDays} Days`}
                      <FontAwesomeIcon icon={solid('chevron-down')} size='sm' className='ml-2' />
                    </div>
                  )}
                >
                  <Dropdown.Item onClick={() => setMetricDays(7)}>7 Days</Dropdown.Item>
                  <Dropdown.Item onClick={() => setMetricDays(14)}>14 Days</Dropdown.Item>
                  <Dropdown.Item onClick={() => setMetricDays(30)}>30 Days</Dropdown.Item>
                  <Dropdown.Item onClick={() => setMetricDays(90)}>90 Days</Dropdown.Item>
                </Dropdown>
              </div>
            </div>
            <div className='flex grow flex-col text-center'>
              {metricsAreLoading && <LoadingSpinner />}
              {metricsAreLoading || aggregatedMetrics === undefined || (
                <div className='text-md flex justify-between text-gray-500'>
                  {store.show_attribution_metrics &&
                    usecase.show_attribution_metrics &&
                    Boolean(aggregatedMetrics.centsDriven) && (
                      <div>
                        Attributed Revenue:{' '}
                        <span className='text-black'>
                          {Math.round((aggregatedMetrics?.centsDriven || 0) / 100).toLocaleString(
                            'en-US',
                            {
                              style: 'currency',
                              currency: 'USD',
                              maximumFractionDigits: 0,
                              useGrouping: true,
                            },
                          )}
                        </span>
                      </div>
                    )}
                  <div>
                    Delivered Messages:{' '}
                    <span className='text-black'>
                      {(aggregatedMetrics?.sentMessages ?? 0).toLocaleString('en', {
                        useGrouping: true,
                      })}
                    </span>
                  </div>
                  <div>
                    Open Rate:{' '}
                    <span className='text-black'>
                      {(aggregatedMetrics?.openRate ?? 0).toLocaleString('en-US', {
                        style: 'percent',
                        maximumFractionDigits: 1,
                        useGrouping: true,
                      })}
                    </span>
                  </div>
                  <div>
                    Click Rate:{' '}
                    <span className='text-black'>
                      {(aggregatedMetrics?.clickRate ?? 0).toLocaleString('en-US', {
                        style: 'percent',
                        maximumFractionDigits: 1,
                        useGrouping: true,
                      })}
                    </span>
                  </div>
                </div>
              )}
              <MetricsHeaderChart metrics={dailyMetrics} height={265} />
            </div>
          </Card>
        </div>
        <Card className='grow'>
          <span className='text-xl font-medium'>Responses</span>
          <FeedbackTable
            isLoading={isLoadingFeedbacks}
            feedbacks={feedbacks?.results ?? []}
            currentPage={page}
            setPage={setPage}
            totalPages={Math.ceil((feedbacks?.count ?? 1) / 10)}
          />
        </Card>
      </div>
    </div>
  );
}

export function DashboardVideoFlow() {
  const { store } = useOutletContext() as DashboardContext;
  const loaderUsecase = useLoaderData() as LoaderData<typeof loaders.usecaseLoader>;
  const storeKeyName = store?.key_name;
  const { data: usecase } = useQuery({
    ...queries.usecase(loaderUsecase.id),
    initialData: loaderUsecase,
    enabled: !!storeKeyName,
  });

  if (!store) {
    return null;
  }
  if (usecase.onboarding_state === 'LIVE') {
    return <UseCaseLive usecase={usecase} store={store} />;
  } else if (usecase.onboarding_state === 'VIDEOS_GREENLIT') {
    return <UseCaseIntegration usecase={usecase} />;
  } else if (usecase.onboarding_state === 'VIDEOS_RECEIVED') {
    return <UseCaseProcessing usecase={usecase} />;
  } else {
    return <UploadVideo usecase={usecase} />;
  }
}
